import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Copy, KeyRound, Loader2, Pencil, Plus, Trash2, TriangleAlert } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useApiAppsApi, useCompaniesApi } from '@/services/api';
import { t } from '@/i18n';
import type { ApiApplicationDto, CompanyListItem } from '@ip/shared-types';

const SCOPE_PL = 'company:lookup:pl';
const SCOPE_EU = 'company:lookup:eu';
const SCOPE_LEADS = 'crm:leads:write';
const COLUMN_COUNT = 7;
/**
 * Company a token is bound to. Options are the companies the caller can see
 * ("Name — NIP", the stored value is the NIP) plus the global, lookups-only
 * token. `value` is the DTO shape (`string | null`); the Radix sentinel for
 * "no company" (an item with value '' is forbidden) never leaves this
 * component. A value not in the list — the list failed to load — is still
 * offered as its own option so saving the dialog never silently detaches it.
 */
const GLOBAL = '__global__';

interface CompanySelectProps {
  id: string;
  value: string | null;
  onChange: (value: string | null) => void;
  companies: CompanyListItem[];
  loading: boolean;
}

function CompanySelect({ id, value, onChange, companies, loading }: CompanySelectProps) {
  const outOfList = value !== null && !companies.some((c) => c.nip === value);
  return (
    <div className="space-y-1">
      <Label htmlFor={id}>{t('apiApps.companyNip')}</Label>
      <Select
        value={value ?? GLOBAL}
        onValueChange={(v) => onChange(v === GLOBAL ? null : v)}
        disabled={loading}
      >
        <SelectTrigger id={id} className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={GLOBAL}>{t('apiApps.companyGlobal')}</SelectItem>
          {companies.map((c) => (
            <SelectItem key={c.nip} value={c.nip}>
              {c.name} <span className="text-muted-foreground">— {c.nip}</span>
            </SelectItem>
          ))}
          {outOfList && (
            <SelectItem value={value}>
              {value} <span className="text-muted-foreground">{t('apiApps.companyOutOfList')}</span>
            </SelectItem>
          )}
        </SelectContent>
      </Select>
      <p className="text-muted-foreground text-xs">{t('apiApps.companyNipHint')}</p>
    </div>
  );
}

/** Short badge label for a scope — the full scope string stays in `title`. */
function scopeLabel(scope: string): string {
  if (scope === SCOPE_PL) return 'PL';
  if (scope === SCOPE_EU) return 'EU';
  if (scope === SCOPE_LEADS) return 'CRM';
  return scope;
}

/** ISO date-time -> `YYYY-MM-DD` for a native date input (local wall clock). */
function toDateInputValue(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * The chosen day is an INCLUSIVE last day of validity — send its local end of
 * day so a token picked for "31.12" still works throughout 31.12.
 */
function toExpiryIso(dateInput: string): string | null {
  return dateInput ? new Date(`${dateInput}T23:59:59`).toISOString() : null;
}

function isExpired(app: ApiApplicationDto): boolean {
  return app.expiresAt !== null && new Date(app.expiresAt).getTime() < Date.now();
}

async function copyToClipboard(value: string, successKey: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(t(successKey));
  } catch {
    toast.error(t('common.error'));
  }
}

/**
 * Superadmin panel for the global API applications (`pap_…` tokens) that let
 * external systems call the registry-lookup endpoints. The plaintext token is
 * returned exactly once — on create and on regenerate — so both flows end in
 * the one-time reveal dialog; the list only ever shows the stored prefix.
 */
export function ApiApplications() {
  const { listApiApps, createApiApp, updateApiApp, regenerateApiAppToken, deleteApiApp } =
    useApiAppsApi();
  const { getCompanies } = useCompaniesApi();

  const [apps, setApps] = useState<ApiApplicationDto[]>([]);
  // null = not loaded yet (or the load failed) — the next dialog open retries.
  const [companies, setCompanies] = useState<CompanyListItem[] | null>(null);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [scopePl, setScopePl] = useState(true);
  const [scopeEu, setScopeEu] = useState(true);
  const [scopeLeads, setScopeLeads] = useState(false);
  const [companyNip, setCompanyNip] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState('');
  const [creating, setCreating] = useState(false);

  const [editTarget, setEditTarget] = useState<ApiApplicationDto | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editScopePl, setEditScopePl] = useState(false);
  const [editScopeEu, setEditScopeEu] = useState(false);
  const [editScopeLeads, setEditScopeLeads] = useState(false);
  const [editCompanyNip, setEditCompanyNip] = useState<string | null>(null);
  const [editExpiresAt, setEditExpiresAt] = useState('');
  const [editActive, setEditActive] = useState(true);
  const [saving, setSaving] = useState(false);

  const [tokenReveal, setTokenReveal] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ApiApplicationDto | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [regenTarget, setRegenTarget] = useState<ApiApplicationDto | null>(null);
  const [regenerating, setRegenerating] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      setApps(await listApiApps());
    } catch (error) {
      toast.error(t('errors.loadingFailed'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [listApiApps]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  /**
   * Picker options, fetched on first dialog open rather than on mount: the
   * sidebar already fetches /companies on every navigation, so by the time
   * anyone opens a dialog the 60 s server cache is warm.
   */
  const ensureCompanies = async () => {
    if (companies !== null || companiesLoading) return;
    try {
      setCompaniesLoading(true);
      setCompanies((await getCompanies()).filter((c) => c.nip !== 'other'));
    } catch (error) {
      toast.error(t('errors.loadingFailed'));
      console.error(error);
    } finally {
      setCompaniesLoading(false);
    }
  };

  const openCreate = () => {
    setName('');
    setDescription('');
    setScopePl(true);
    setScopeEu(true);
    setScopeLeads(false);
    setCompanyNip(null);
    setExpiresAt('');
    void ensureCompanies();
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error(t('apiApps.nameRequired'));
      return;
    }
    const scopes = [
      ...(scopePl ? [SCOPE_PL] : []),
      ...(scopeEu ? [SCOPE_EU] : []),
      ...(scopeLeads ? [SCOPE_LEADS] : []),
    ];
    if (scopes.length === 0) {
      toast.error(t('apiApps.scopesRequired'));
      return;
    }
    // A lead has to land in one specific CRM — the backend refuses the scope
    // without a company, so catch it here rather than as a 400.
    if (scopeLeads && !companyNip) {
      toast.error(t('apiApps.companyNipRequired'));
      return;
    }
    setCreating(true);
    try {
      const { token, dto } = await createApiApp({
        name: name.trim(),
        description: description.trim() || null,
        scopes,
        companyNip,
        expiresAt: toExpiryIso(expiresAt),
      });
      setApps((prev) => [dto, ...prev]);
      setCreateOpen(false);
      toast.success(t('apiApps.createSuccess'));
      setTokenReveal(token);
    } catch (error) {
      toast.error(error instanceof Error && error.message ? error.message : t('errors.saveFailed'));
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (app: ApiApplicationDto) => {
    setEditTarget(app);
    setEditName(app.name);
    setEditDescription(app.description ?? '');
    setEditScopePl(app.scopes.includes(SCOPE_PL));
    setEditScopeEu(app.scopes.includes(SCOPE_EU));
    setEditScopeLeads(app.scopes.includes(SCOPE_LEADS));
    setEditCompanyNip(app.companyNip);
    setEditExpiresAt(toDateInputValue(app.expiresAt));
    setEditActive(app.active);
    void ensureCompanies();
  };

  const handleUpdate = async () => {
    if (!editTarget) return;
    if (!editName.trim()) {
      toast.error(t('apiApps.nameRequired'));
      return;
    }
    const scopes = [
      ...(editScopePl ? [SCOPE_PL] : []),
      ...(editScopeEu ? [SCOPE_EU] : []),
      ...(editScopeLeads ? [SCOPE_LEADS] : []),
    ];
    if (scopes.length === 0) {
      toast.error(t('apiApps.scopesRequired'));
      return;
    }
    if (editScopeLeads && !editCompanyNip) {
      toast.error(t('apiApps.companyNipRequired'));
      return;
    }
    setSaving(true);
    try {
      const dto = await updateApiApp(editTarget.id, {
        name: editName.trim(),
        description: editDescription.trim() || null,
        scopes,
        companyNip: editCompanyNip,
        expiresAt: toExpiryIso(editExpiresAt),
        active: editActive,
      });
      setApps((prev) => prev.map((app) => (app.id === dto.id ? dto : app)));
      setEditTarget(null);
      toast.success(t('apiApps.updateSuccess'));
    } catch (error) {
      toast.error(error instanceof Error && error.message ? error.message : t('errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerate = async () => {
    if (!regenTarget) return;
    setRegenerating(true);
    try {
      const { token, dto } = await regenerateApiAppToken(regenTarget.id);
      setApps((prev) => prev.map((app) => (app.id === dto.id ? dto : app)));
      setRegenTarget(null);
      toast.success(t('apiApps.regenerateSuccess'));
      setTokenReveal(token);
    } catch (error) {
      toast.error(error instanceof Error && error.message ? error.message : t('errors.saveFailed'));
    } finally {
      setRegenerating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteApiApp(deleteTarget.id);
      setApps((prev) => prev.filter((app) => app.id !== deleteTarget.id));
      setDeleteTarget(null);
      toast.success(t('apiApps.deleteSuccess'));
    } catch (error) {
      toast.error(
        error instanceof Error && error.message ? error.message : t('errors.deleteFailed'),
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">{t('apiApps.title')}</h1>
          <p className="text-muted-foreground text-sm">{t('apiApps.description')}</p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="size-4" />
          {t('apiApps.addApp')}
        </Button>
      </div>

      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="text-muted-foreground whitespace-nowrap">
                {t('apiApps.name')}
              </TableHead>
              <TableHead className="text-muted-foreground whitespace-nowrap">
                {t('apiApps.tokenPrefix')}
              </TableHead>
              <TableHead className="text-muted-foreground whitespace-nowrap">
                {t('apiApps.scopes')}
              </TableHead>
              <TableHead className="text-muted-foreground whitespace-nowrap">
                {t('apiApps.expiresAt')}
              </TableHead>
              <TableHead className="text-muted-foreground whitespace-nowrap">
                {t('apiApps.lastUsed')}
              </TableHead>
              <TableHead className="text-muted-foreground whitespace-nowrap">
                {t('apiApps.status')}
              </TableHead>
              <TableHead className="text-muted-foreground text-right whitespace-nowrap">
                {t('common.actions')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 4 }, (_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={COLUMN_COUNT}>
                    <Skeleton className="h-8 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : apps.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} className="h-40 text-center">
                  <p className="text-muted-foreground">{t('apiApps.empty')}</p>
                </TableCell>
              </TableRow>
            ) : (
              apps.map((app) => (
                <TableRow key={app.id}>
                  <TableCell className="py-2">
                    <p className="font-medium">{app.name}</p>
                    {app.description && (
                      <p className="text-muted-foreground text-xs">{app.description}</p>
                    )}
                  </TableCell>
                  <TableCell className="py-2 font-mono text-xs">{app.tokenPrefix}…</TableCell>
                  <TableCell className="py-2">
                    <div className="flex flex-wrap gap-1">
                      {app.scopes.map((scope) => (
                        <Badge key={scope} variant="secondary" title={scope}>
                          {scopeLabel(scope)}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="py-2 text-xs whitespace-nowrap">
                    {app.expiresAt === null ? (
                      <span className="text-muted-foreground">{t('apiApps.neverExpires')}</span>
                    ) : isExpired(app) ? (
                      <span className="text-destructive">
                        {new Date(app.expiresAt).toLocaleDateString('pl-PL')} ·{' '}
                        {t('apiApps.expired')}
                      </span>
                    ) : (
                      new Date(app.expiresAt).toLocaleDateString('pl-PL')
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground py-2 text-xs whitespace-nowrap">
                    {app.lastUsedAt
                      ? new Date(app.lastUsedAt).toLocaleString('pl-PL')
                      : t('apiApps.neverUsed')}
                  </TableCell>
                  <TableCell className="py-2">
                    <Badge variant={app.active ? 'default' : 'outline'}>
                      {app.active ? t('apiApps.active') : t('apiApps.inactive')}
                    </Badge>
                  </TableCell>
                  <TableCell className="py-2 text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={t('common.edit')}
                        onClick={() => openEdit(app)}
                      >
                        <Pencil className="size-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={t('apiApps.regenerate')}
                        onClick={() => setRegenTarget(app)}
                      >
                        <KeyRound className="size-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="text-destructive hover:text-destructive"
                        aria-label={t('apiApps.delete')}
                        onClick={() => setDeleteTarget(app)}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create application */}
      <Dialog
        open={createOpen}
        onOpenChange={(open) => {
          if (!creating) setCreateOpen(open);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('apiApps.createTitle')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="api-app-name">{t('apiApps.name')}</Label>
              <Input id="api-app-name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="api-app-description">{t('apiApps.descriptionLabel')}</Label>
              <Input
                id="api-app-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('apiApps.scopes')}</Label>
              <Label className="flex items-center gap-2 font-normal">
                <Checkbox checked={scopePl} onCheckedChange={(c) => setScopePl(c === true)} />
                {t('apiApps.scopeCompanyPl')}
              </Label>
              <Label className="flex items-center gap-2 font-normal">
                <Checkbox checked={scopeEu} onCheckedChange={(c) => setScopeEu(c === true)} />
                {t('apiApps.scopeCompanyEu')}
              </Label>
              <Label className="flex items-center gap-2 font-normal">
                <Checkbox checked={scopeLeads} onCheckedChange={(c) => setScopeLeads(c === true)} />
                {t('apiApps.scopeCrmLeads')}
              </Label>
            </div>
            <CompanySelect
              id="api-app-company-nip"
              value={companyNip}
              onChange={setCompanyNip}
              companies={companies ?? []}
              loading={companiesLoading}
            />
            <div className="space-y-1">
              <Label htmlFor="api-app-expires">{t('apiApps.expiresAt')}</Label>
              <Input
                id="api-app-expires"
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
              />
              {/* Empty date = no expiry; spell that out instead of leaving a blank field ambiguous. */}
              {!expiresAt && <p className="text-muted-foreground text-xs">{t('apiApps.neverExpires')}</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={creating}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleCreate} disabled={creating || !name.trim()}>
              {creating && <Loader2 className="size-4 animate-spin" />}
              {t('apiApps.addApp')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit application */}
      <Dialog
        open={editTarget !== null}
        onOpenChange={(open) => {
          if (!saving && !open) setEditTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('apiApps.editTitle')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="edit-api-app-name">{t('apiApps.name')}</Label>
              <Input
                id="edit-api-app-name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="edit-api-app-description">{t('apiApps.descriptionLabel')}</Label>
              <Input
                id="edit-api-app-description"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('apiApps.scopes')}</Label>
              <Label className="flex items-center gap-2 font-normal">
                <Checkbox
                  checked={editScopePl}
                  onCheckedChange={(c) => setEditScopePl(c === true)}
                />
                {t('apiApps.scopeCompanyPl')}
              </Label>
              <Label className="flex items-center gap-2 font-normal">
                <Checkbox
                  checked={editScopeEu}
                  onCheckedChange={(c) => setEditScopeEu(c === true)}
                />
                {t('apiApps.scopeCompanyEu')}
              </Label>
              <Label className="flex items-center gap-2 font-normal">
                <Checkbox
                  checked={editScopeLeads}
                  onCheckedChange={(c) => setEditScopeLeads(c === true)}
                />
                {t('apiApps.scopeCrmLeads')}
              </Label>
            </div>
            <CompanySelect
              id="edit-api-app-company-nip"
              value={editCompanyNip}
              onChange={setEditCompanyNip}
              companies={companies ?? []}
              loading={companiesLoading}
            />
            <div className="space-y-1">
              <Label htmlFor="edit-api-app-expires">{t('apiApps.expiresAt')}</Label>
              <Input
                id="edit-api-app-expires"
                type="date"
                value={editExpiresAt}
                onChange={(e) => setEditExpiresAt(e.target.value)}
              />
              {!editExpiresAt && (
                <p className="text-muted-foreground text-xs">{t('apiApps.neverExpires')}</p>
              )}
            </div>
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor="edit-api-app-active">{t('apiApps.active')}</Label>
              <Switch id="edit-api-app-active" checked={editActive} onCheckedChange={setEditActive} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)} disabled={saving}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleUpdate} disabled={saving || !editName.trim()}>
              {saving && <Loader2 className="size-4 animate-spin" />}
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Regenerate token confirm */}
      <AlertDialog
        open={regenTarget !== null}
        onOpenChange={(open) => {
          if (!open && !regenerating) setRegenTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('apiApps.regenerateConfirmTitle', { name: regenTarget?.name ?? '' })}
            </AlertDialogTitle>
            <AlertDialogDescription>{t('apiApps.regenerateConfirmMessage')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={regenerating}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction disabled={regenerating} variant="destructive" onClick={handleRegenerate}>
              {regenerating && <Loader2 className="size-4 animate-spin" />}
              {t('apiApps.regenerate')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete application confirm */}
      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open && !deleting) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('apiApps.deleteConfirmTitle', { name: deleteTarget?.name ?? '' })}
            </AlertDialogTitle>
            <AlertDialogDescription>{t('apiApps.deleteConfirmMessage')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction disabled={deleting} variant="destructive" onClick={handleDelete}>
              {deleting && <Loader2 className="size-4 animate-spin" />}
              {t('apiApps.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* One-time token reveal */}
      <AlertDialog open={tokenReveal !== null} onOpenChange={(open) => !open && setTokenReveal(null)}>
        <AlertDialogContent className="sm:max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('apiApps.tokenRevealTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('apiApps.tokenRevealDescription')}</AlertDialogDescription>
          </AlertDialogHeader>
          {tokenReveal && (
            <div className="space-y-3 text-left">
              <div className="flex items-start gap-2 rounded-md border border-amber-500/50 bg-amber-500/10 p-2.5">
                <TriangleAlert className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" />
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  {t('apiApps.tokenRevealWarning')}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <code className="bg-muted flex-1 overflow-x-auto rounded px-2 py-1.5 font-mono text-xs">
                  {tokenReveal}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(tokenReveal, 'apiApps.tokenCopied')}
                >
                  <Copy className="size-4" />
                  {t('apiApps.copyToken')}
                </Button>
              </div>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setTokenReveal(null)}>
              {t('apiApps.close')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
